import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { Badge } from '@/shared/ui/badge';
import { Loader2, User as UserIcon, X } from 'lucide-react';
import { useUpdateUserOrganizations, useUserOrganizations } from '@/domains/users/services/account-users';
import { Organization, UpdateUserOrganizationsData, User } from '@/shared/types/userTypes';
import { Role, getRoleName } from '@/shared/types/userRoleTypes';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import SearchBar from '@/shared/components/SearchBar';
import { useUserRoles } from '@/shared/hooks/userRoles';

interface ManageOrganizationUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  user: User;
  currentUserId: string;
}

const ManageOrganizationUserDialog: React.FC<ManageOrganizationUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
  user,
  currentUserId,
}) => {
  // Account management
  const [selectedOrganizations, setSelectedOrganizations] = useState<Organization[]>([]);
  const [organizationRoleMap, setOrganizationRoleMap] = useState<Record<string, Role>>({});
  const [defaultOrganizationId, setDefaultOrganizationId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // UI state
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  const { data: currentUserOrganizations, isLoading: isLoadingOrganizations } = useUserOrganizations(open ? currentUserId : undefined);
  const updateUser = useUpdateUserOrganizations();

  // Get available roles based on permissions
  const { tenantUserRoles: availableRoles } = useUserRoles();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open && user) {
      // Initialize with user's existing accounts that are also in the current user's accounts and have at least one role that is available
      const userOrganizations = user.organizations ? [...user.organizations.filter(a => currentUserOrganizations?.some(ca => ca.organizationId === a.organizationId))].filter(a => availableRoles.includes(a.role)) : [];
      setSelectedOrganizations(userOrganizations);

      // Initialize account roles - use roles from user.accounts for each account
      const roleMap: Record<string, Role> = {};
      userOrganizations.forEach(organization => {
        roleMap[organization.organizationId] = user.organizations?.find(a => a.organizationId === organization.organizationId)?.role || availableRoles[0] || Role.VIEWER;
      });
      setOrganizationRoleMap(roleMap);

      // Initialize default account only when user has one marked isDefault (do not fallback to first)
      const initialDefault = user.organizations?.find(a => a.isDefault)?.organizationId ?? null;
      setDefaultOrganizationId(initialDefault);

      setSearchTerm('');
      setErrors({});
    }
  }, [open, user, currentUserOrganizations, availableRoles]);

  // Filter accounts based on search term; selected organizations appear first
  const filteredOrganizations = useMemo(() => {
    const selectedIds = new Set(selectedOrganizations.map((o) => o.organizationId));
    const filtered = (currentUserOrganizations ?? []).filter((organization) =>
      organization.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      organization.organizationId?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return [...filtered].sort((a, b) => {
      const aSelected = selectedIds.has(a.organizationId);
      const bSelected = selectedIds.has(b.organizationId);
      if (aSelected === bSelected) {
        return a.name.localeCompare(b.name);
      }
      return aSelected ? -1 : 1;
    });
  }, [currentUserOrganizations, searchTerm, selectedOrganizations]);

  const handleOrganizationToggle = (organization: Organization) => {
    setSelectedOrganizations(prev => {
      const isSelected = prev.some(o => o.organizationId === organization.organizationId);
      const newSelected = isSelected
        ? prev.filter(o => o.organizationId !== organization.organizationId)
        : [...prev, organization];

      // Update default account when selection changes
      if (isSelected) {
        // Removing organization: clear default only if it was this one (do not set to another)
        setDefaultOrganizationId(current => (current === organization.organizationId ? null : current));
        // Remove role when account is deselected
        setOrganizationRoleMap(prev => {
          const updated = { ...prev };
          delete updated[organization.organizationId];
          return updated;
        });
      } else {
        // Adding organization: do not auto-set default; user picks via radio list
        // Add roles when account is selected - use roles from user.accounts if available
        const userOrganizationRole = user.organizations?.find(a => a.organizationId === organization.organizationId)?.role;
        setOrganizationRoleMap(prev => ({
          ...prev,
          [organization.organizationId]: userOrganizationRole
            ? userOrganizationRole
            : availableRoles[0] || Role.VIEWER,
        }));
      }

      return newSelected;
    });
  };

  const handleOrganizationRoleChange = (organizationId: string, role: Role) => {
    setOrganizationRoleMap(prev => ({
      ...prev,
      [organizationId]: role,
    }));
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleSubmit = async () => {
    try {
      const userData: UpdateUserOrganizationsData = {
        organizations: selectedOrganizations.map(organization => ({
          organizationId: organization.organizationId,
          role: organizationRoleMap[organization.organizationId] || availableRoles[0] || Role.VIEWER,
          isDefault: organization.organizationId === defaultOrganizationId,
        })),
      };

      await updateUser.mutateAsync({
        userId: user.userId,
        userData: userData,
      });

      showSuccess(SUCCESS.USER.UPDATED);
      onSuccess?.();
      onClose();
    } catch (error) {
      showErrorWithApiError(error, ERROR.USER.UPDATE);
    }
  };

  const hasChanges = () => {
    const organizationsChanged = JSON.stringify(selectedOrganizations.map(o => o.organizationId).sort((a, b) => a.localeCompare(b))) !==
      JSON.stringify((user.organizations || []).map(o => o.organizationId).sort((a, b) => a.localeCompare(b)));

    const rolesChanged = selectedOrganizations.some(organization => {
      const userOrganizationRole = user.organizations?.find(a => a.organizationId === organization.organizationId)?.role || availableRoles[0] || Role.VIEWER;
      const currentRole = organizationRoleMap[organization.organizationId] || userOrganizationRole;
      // Compare arrays by converting to sorted strings
      const currentRoleStr = JSON.stringify([...currentRole].sort((a, b) => a.localeCompare(b)));
      const userRoleStr = JSON.stringify([...userOrganizationRole].sort((a, b) => a.localeCompare(b)));
      return currentRoleStr !== userRoleStr;
    });

    const currentDefault = defaultOrganizationId ?? null;
    const originalDefault = user.organizations?.find(o => o.isDefault)?.organizationId ?? null;
    const defaultChanged = currentDefault !== originalDefault;

    return organizationsChanged || rolesChanged || defaultChanged;
  };

  const canSubmit =
    user &&
    !updateUser.isPending &&
    hasChanges() &&
    (selectedOrganizations.length > 0 ? !!defaultOrganizationId : true); // default is required when there are selected organizations

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Manage user's accounts and roles
          </DialogTitle>
        </DialogHeader>

        {user && (
          <div className="space-y-4">
            {/* User Info */}
            <div className="flex items-center space-x-2 border-b pb-2">
              <UserIcon className="h-5 w-5" />
              <div className="text-left pb-2">
                <p className="text-sm font-medium">{user.name}</p>
                <p className="text-xs">{user.email}</p>
              </div>
            </div>
            {isLoadingOrganizations ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2">Loading organizations...</span>
              </div>
            ) : filteredOrganizations && filteredOrganizations.length > 0 ? (
              <div className="space-y-4">
                {/* Search Input */}
                <div className="relative">
                  <SearchBar value={searchTerm} onChange={(value) => setSearchTerm(value)} placeholder="Search organizations..." />
                  {searchTerm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearSearch}
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                    >
                      <X className="h-6 w-6" />
                    </Button>
                  )}
                </div>

                {/* Account Checkboxes with Role Selection */}
                <div className="space-y-3 pb-2 max-h-60 overflow-y-auto">
                  {filteredOrganizations.length > 0 ? (
                    filteredOrganizations.map((organization) => {
                      const isSelected = selectedOrganizations.some(o => o.organizationId === organization.organizationId);
                      const isDefaultOrganization = defaultOrganizationId === organization.organizationId;
                      const showDefaultBadge = isSelected && isDefaultOrganization;
                      // Get roles from user.organizations for this specific organization, or use the roles from organizationRoles state
                      const userOrganizationRole = user.organizations?.find(a => a.organizationId === organization.organizationId)?.role || availableRoles[0] || Role.VIEWER;
                      const currentOrganizationRole = organizationRoleMap[organization.organizationId] || userOrganizationRole;
                      // For the Select component, use the first role (since it only supports single selection)
                      // Fallback to first available role if no roles are set
                      const organizationRole = currentOrganizationRole;

                      return (
                        <div key={organization.organizationId} className="space-y-2 p-3 border rounded-lg hover:bg-muted transition-colors">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={organization.organizationId}
                              checked={isSelected}
                              onCheckedChange={() => handleOrganizationToggle(organization)}
                            />
                            <Label htmlFor={organization.organizationId} className="flex-1 cursor-pointer">
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex flex-col items-start">
                                  <p className="text-sm font-medium">{organization.name}</p>
                                  <p className="text-xs">ID: {organization.organizationId}</p>
                                </div>
                                {showDefaultBadge && (
                                  <Badge variant="default" className="border ml-2">Default</Badge>
                                )}
                              </div>
                            </Label>
                          </div>

                          {/* Role Selection for Selected Accounts */}
                          {isSelected && (
                            <div className="ml-6 space-y-2">
                              <Label htmlFor={`role-${organization.organizationId}`} className="text-xs">
                                Role for this organization:
                              </Label>
                              <Select
                                value={organizationRole}
                                onValueChange={(value) => handleOrganizationRoleChange(organization.organizationId, value as Role)}
                              >
                                <SelectTrigger id={`role-${organization.organizationId}`} className="w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableRoles.map((role) => (
                                    <SelectItem key={role} value={role}>
                                      {getRoleName(role)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8">
                      {searchTerm ? 'No accounts found matching your search.' : 'No accounts available'}
                    </div>
                  )}
                </div>

                {/* Default account – radio list of selected accounts */}
                {selectedOrganizations.length > 0 && (
                  <div className="space-y-3 pt-2 border-t">
                    <Label className="text-sm font-medium">Default account</Label>
                    <p className="text-xs">
                      Choose which account is the default for this user.
                    </p>
                    <RadioGroup
                      value={defaultOrganizationId ?? ''}
                      onValueChange={(value) => setDefaultOrganizationId(value || null)}
                      className="space-y-2"
                    >
                      {selectedOrganizations.map((organization) => (
                        <div
                          key={organization.organizationId}
                          className="flex items-center space-x-2 rounded-lg border p-2 hover:bg-muted/50"
                        >
                          <RadioGroupItem
                            value={organization.organizationId}
                            id={`default-${organization.organizationId}`}
                          />
                          <Label
                            htmlFor={`default-${organization.organizationId}`}
                            className="flex-1 cursor-pointer text-sm font-normal"
                          >
                            <div className="flex flex-col">
                              <span>{organization.name}</span>
                              <span className="text-xs">ID: {organization.organizationId}</span>
                            </div>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                No accounts available
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={onClose} disabled={updateUser.isPending}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="min-w-[100px]"
              >
                {updateUser.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManageOrganizationUserDialog; 